"use client";

import { useState } from "react";

import { WidgetOptionButton } from "../widget-option-button";

import { EMBED_ATTRIBUTION_COLOR, EMBED_FRAME_BORDER } from "../embed-snippet-style";

const POPULAR_CITIES = [
  { slug: "new-delhi", name: "New Delhi", nameHi: "नई दिल्ली" },
  { slug: "mumbai", name: "Mumbai", nameHi: "मुंबई" },
  { slug: "bangalore", name: "Bengaluru", nameHi: "बेंगलुरु" },
  { slug: "kolkata", name: "Kolkata", nameHi: "कोलकाता" },
  { slug: "chennai", name: "Chennai", nameHi: "चेन्नई" },
  { slug: "jaipur", name: "Jaipur", nameHi: "जयपुर" },
  { slug: "hyderabad", name: "Hyderabad", nameHi: "हैदराबाद" },
  { slug: "ahmedabad", name: "Ahmedabad", nameHi: "अहमदाबाद" },
  { slug: "pune", name: "Pune", nameHi: "पुणे" },
  { slug: "varanasi", name: "Varanasi", nameHi: "वाराणसी" },
  { slug: "lucknow", name: "Lucknow", nameHi: "लखनऊ" },
  { slug: "indore", name: "Indore", nameHi: "इंदौर" },
];

export default function PanchangWidgetCustomizer() {
  const [city, setCity] = useState("new-delhi");
  const [lang, setLang] = useState<"en" | "hi">("en");
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const [copied, setCopied] = useState(false);

  const previewUrl = `/embed/panchang?city=${city}&lang=${lang}&theme=${theme}`;
  const embedCode = `<!-- Ishvaram Vedic Panchang Widget -->
<iframe
  src="https://ishvaram.com/embed/panchang?city=${city}&lang=${lang}&theme=${theme}"
  width="100%"
  height="380"
  style="border: 1px solid ${EMBED_FRAME_BORDER}; border-radius: 12px; max-width: 480px; width: 100%; display: block;"
  frameborder="0"
  loading="lazy"
  title="Daily Vedic Panchang"
></iframe>
<div style="font-size: 11px; text-align: center; margin-top: 6px; color: ${EMBED_ATTRIBUTION_COLOR};">
  Panchang data by <a href="https://ishvaram.com/panchang/" target="_blank" rel="noopener" style="color: ${EMBED_ATTRIBUTION_COLOR}; text-decoration: underline;">Ishvaram Vedic Astrology</a>
</div>`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback
    }
  };

  return (
    <div className="grid gap-8 lg:grid-cols-12 items-start">
      {/* Controls & Code Generator */}
      <div className="lg:col-span-6 rounded-2xl border border-border-default bg-surface-elevated p-6 shadow-sm">
        <h2 className="text-h3 font-serif font-bold text-text-primary mb-4">
          Customize Your Widget
        </h2>

        <div className="space-y-4 text-body-small">
          {/* City Selector */}
          <div>
            <label className="block font-medium text-text-primary mb-1.5">
              Select City / Location
            </label>
            <select
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full rounded-lg border border-border-default bg-surface-base px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              {POPULAR_CITIES.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {lang === "hi" ? `${c.nameHi} (${c.name})` : c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Language Selector */}
          <div>
            <label className="block font-medium text-text-primary mb-1.5">Language</label>
            <div className="grid grid-cols-2 gap-2">
              <WidgetOptionButton active={lang === "en"} onClick={() => setLang("en")}>
                English
              </WidgetOptionButton>
              <WidgetOptionButton active={lang === "hi"} onClick={() => setLang("hi")}>
                हिंदी (Hindi)
              </WidgetOptionButton>
            </div>
          </div>

          {/* Theme Selector */}
          <div>
            <label className="block font-medium text-text-primary mb-1.5">Color Theme</label>
            <div className="grid grid-cols-2 gap-2">
              <WidgetOptionButton active={theme === "light"} onClick={() => setTheme("light")}>
                ☀️ Light Mode
              </WidgetOptionButton>
              <WidgetOptionButton active={theme === "dark"} onClick={() => setTheme("dark")}>
                🌙 Dark Mode
              </WidgetOptionButton>
            </div>
          </div>

          {/* Embed Code Snippet */}
          <div className="pt-2">
            <div className="flex items-center justify-between mb-1.5">
              <label className="font-medium text-text-primary">HTML Embed Code</label>
              <button
                type="button"
                onClick={handleCopy}
                className="text-xs font-semibold text-brand-primary hover:underline"
              >
                {copied ? "✓ Copied to Clipboard!" : "Copy Code"}
              </button>
            </div>
            <textarea
              readOnly
              value={embedCode}
              rows={6}
              className="w-full font-mono text-caption rounded-lg border border-border-default bg-foreground text-background p-3 leading-relaxed focus:outline-none"
            />
            <p className="mt-1.5 text-caption text-text-muted">
              Paste this HTML into any WordPress, Webflow, Ghost, or static site.
            </p>
          </div>
        </div>
      </div>

      {/* Live Widget Preview */}
      <div className="lg:col-span-6 flex flex-col items-center">
        <div className="w-full max-w-[480px]">
          <div className="flex items-center justify-between mb-2 px-1 text-xs text-text-muted">
            <span className="font-medium">Live Widget Preview</span>
            <span>480 × 380 px</span>
          </div>
          <div className="rounded-2xl border border-border-default bg-surface-elevated p-3 shadow-md">
            <iframe
              key={previewUrl}
              src={previewUrl}
              width={480}
              height={380}
              className="w-full rounded-xl border-0"
              title="Panchang Widget Preview"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
